import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, Timer, MapPin, Trash2 } from 'lucide-react';

const TimelineTab = ({ 
  caseId, 
  timeline, 
  canEdit, 
  isAdmin, 
  token, 
  onRefresh 
}) => {
  const [showAddTimeline, setShowAddTimeline] = useState(false);
  const [timelineForm, setTimelineForm] = useState({
    event_type: 'CUSTOM', event_date: new Date().toISOString().split('T')[0],
    event_time: '', location: '', description: ''
  });

  // Iter132 — refresh the case timeline every 30 minutes so new events,
  // notes, logs, photos and evidence surface without a manual reload.
  // Uses the parent-provided onRefresh fn (same fn Add Event triggers).
  React.useEffect(() => {
    if (!onRefresh) return;
    const id = setInterval(() => { try { onRefresh(); } catch {} }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [onRefresh]);

  const handleAddTimelineEvent = async () => {
    if (!timelineForm.description) {
      toast({ title: 'Error', description: 'Description is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/timeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(timelineForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Timeline event added' });
        setShowAddTimeline(false);
        setTimelineForm({ event_type: 'CUSTOM', event_date: new Date().toISOString().split('T')[0], event_time: '', location: '', description: '' });
        onRefresh();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteTimelineEvent = async (eventId) => {
    if (!eventId.startsWith('TLE-')) return; // Only delete manual events
    if (!window.confirm('Delete this timeline event?')) return;
    await fetch(`${BACKEND_URL}/api/timeline/${eventId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    onRefresh();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white" onClick={() => setShowAddTimeline(true)} data-testid="add-timeline-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Event
          </Button>
        </div>
      )}
      {timeline.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <Timer className="w-12 h-12 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">No timeline events yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-300"></div>
          <div className="space-y-4">
            {timeline.map((event) => (
              <div key={event.id} className="relative pl-10" data-testid={`timeline-event-${event.id}`}>
                <div className="absolute left-2.5 w-3 h-3 rounded-full bg-amber-500 border-2 border-white shadow-sm"></div>
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 text-sm font-medium">{event.event_date}</span>
                        {event.event_time && <span className="text-slate-500 text-sm">{event.event_time}</span>}
                        <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">
                          {event.event_type?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      {event.id.startsWith('TLE-') && isAdmin && (
                        <button onClick={() => handleDeleteTimelineEvent(event.id)} className="text-slate-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {event.location && <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</p>}
                    <p className="text-slate-700 text-sm">{event.description}</p>
                    {event.created_by && <p className="text-xs text-slate-500 mt-2">By {event.created_by}</p>}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Timeline Dialog */}
      <Dialog open={showAddTimeline} onOpenChange={setShowAddTimeline}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Timeline Event</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={timelineForm.event_date} onChange={e => setTimelineForm({ ...timelineForm, event_date: e.target.value })}
                  className="border-slate-300" data-testid="timeline-date-input" />
              </div>
              <div>
                <Label>Time</Label>
                <Input type="time" value={timelineForm.event_time} onChange={e => setTimelineForm({ ...timelineForm, event_time: e.target.value })}
                  className="border-slate-300" />
              </div>
            </div>
            <div>
              <Label>Event Type</Label>
              <Select value={timelineForm.event_type} onValueChange={v => setTimelineForm({ ...timelineForm, event_type: v })}>
                <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SURVEILLANCE">Surveillance</SelectItem>
                  <SelectItem value="INTERVIEW">Interview</SelectItem>
                  <SelectItem value="EVIDENCE_COLLECTED">Evidence Collected</SelectItem>
                  <SelectItem value="MEETING">Meeting</SelectItem>
                  <SelectItem value="INCIDENT">Incident</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={timelineForm.location} onChange={e => setTimelineForm({ ...timelineForm, location: e.target.value })}
                className="border-slate-300" placeholder="Where did this occur?" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={timelineForm.description} onChange={e => setTimelineForm({ ...timelineForm, description: e.target.value })}
                className="border-slate-300" rows={3} placeholder="What happened?" data-testid="timeline-desc-input" />
            </div>
            <Button onClick={handleAddTimelineEvent} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="submit-timeline-btn">
              Add Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimelineTab;
